/**
 * General Tab for Admin Client Detail Page
 * Shows client configuration, typed settings editor, document type assignments, and usage stats.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminEnums } from "@/hooks/use-admin-enums";
import { useAdminApi } from "@/services/api/admin-api";
import type {
    AddOrganizationDocumentTypeRequestDto,
    ClientAdminDetailDto,
    ClientUsageAdminDto,
    DocumentTypeAdminListItemDto,
    OrganizationAdminListItemDto,
    OrganizationDocumentTypeDto,
    OrganizationFileTypeDto,
    UpdateClientRequestDto,
    UpdateClientSettingsRequestDto,
} from "@/types/api/admin";
import { cx } from "@/utils/cx";
import { MANAGEABLE_FILE_TYPES } from "@/utils/file-validation";

// =============================================================================
// TYPES
// =============================================================================

interface GeneralTabProps {
    client: ClientAdminDetailDto;
    onClientUpdated: () => void;
}

// =============================================================================
// CONFIGURATION SECTION
// =============================================================================

const EXPORT_MODE_OPTIONS: SelectItemType[] = [
    { id: "DirectSync", label: "Direct Sync (bridge push)" },
    { id: "Csv", label: "CSV (bridge for lookups only)" },
];

function ConfigurationSection({ client, onClientUpdated }: GeneralTabProps) {
    const adminApi = useAdminApi();
    const { getOptions, isLoaded: enumsLoaded } = useAdminEnums();

    // Form state
    const [name, setName] = useState(client.name);
    const [isActive, setIsActive] = useState(client.isActive);
    const [retentionDays, setRetentionDays] = useState(String(client.retentionDays ?? ""));
    const [retentionMode, setRetentionMode] = useState(client.retentionMode ?? "");
    const [sendExpirationEmail, setSendExpirationEmail] = useState(client.sendExpirationEmail ?? false);
    const [auditRetentionYears, setAuditRetentionYears] = useState(String(client.auditRetentionYears ?? ""));
    const [exportMode, setExportMode] = useState(client.exportMode ?? "DirectSync");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Sync from props when client changes
    useEffect(() => {
        setName(client.name);
        setIsActive(client.isActive);
        setRetentionDays(String(client.retentionDays ?? ""));
        setRetentionMode(client.retentionMode ?? "");
        setSendExpirationEmail(client.sendExpirationEmail ?? false);
        setAuditRetentionYears(String(client.auditRetentionYears ?? ""));
        setExportMode(client.exportMode ?? "DirectSync");
    }, [client]);

    const purgeModeOptions: SelectItemType[] = enumsLoaded ? getOptions("PurgeModeEnum").map((opt) => ({ id: opt.label, label: opt.label })) : [];

    const hasChanges =
        name !== client.name ||
        isActive !== client.isActive ||
        retentionDays !== String(client.retentionDays ?? "") ||
        retentionMode !== (client.retentionMode ?? "") ||
        sendExpirationEmail !== (client.sendExpirationEmail ?? false) ||
        auditRetentionYears !== String(client.auditRetentionYears ?? "") ||
        exportMode !== (client.exportMode ?? "DirectSync");

    const handleSave = useCallback(async () => {
        // Hard retention ceiling: 90 days (the server enforces this too)
        const retentionValue = retentionDays ? Number(retentionDays) : null;
        if (retentionValue !== null && (retentionValue < 1 || retentionValue > 90)) {
            setError("Retention Days must be between 1 and 90 (hard retention ceiling).");
            return;
        }
        setIsSaving(true);
        setError(null);
        setSuccess(false);
        try {
            const dto: UpdateClientRequestDto = {
                name: name.trim(),
                isActive,
                retentionDays: retentionValue,
                retentionMode: retentionMode || null,
                sendExpirationEmail,
                auditRetentionYears: auditRetentionYears ? Number(auditRetentionYears) : null,
                exportMode,
            };
            await adminApi.updateClient(client.id, dto);
            setSuccess(true);
            onClientUpdated();
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update client.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, client.id, name, isActive, retentionDays, retentionMode, sendExpirationEmail, auditRetentionYears, exportMode, onClientUpdated]);

    return (
        <section>
            <h3 className="text-lg font-semibold text-primary">Configuration</h3>
            <p className="mt-1 text-sm text-tertiary">Core client settings and retention policies.</p>

            <div className="mt-4 grid max-w-xl grid-cols-1 gap-4">
                {/* Code (read-only) */}
                <div>
                    <label className="mb-1.5 block text-sm font-medium text-secondary">Code</label>
                    <div className="rounded-lg bg-disabled_subtle px-3 py-2 font-mono text-sm text-disabled">{client.code}</div>
                </div>

                <Input label="Name" value={name} onChange={setName} isRequired size="sm" />

                <Toggle label="Active" isSelected={isActive} onChange={setIsActive} size="sm" />

                <Input label="Retention Days (max 90)" value={retentionDays} onChange={setRetentionDays} placeholder="e.g. 90" size="sm" type="number" />

                {purgeModeOptions.length > 0 && (
                    <Select
                        label="Retention Mode"
                        items={purgeModeOptions}
                        selectedKey={retentionMode || null}
                        onSelectionChange={(key) => setRetentionMode(String(key ?? ""))}
                        placeholder="Select mode..."
                        size="sm"
                    >
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                )}

                <div>
                    <Toggle label="Send Expiration Email" isSelected={sendExpirationEmail} onChange={setSendExpirationEmail} size="sm" />
                    <p className="mt-1 text-xs text-tertiary">
                        Sends the monthly reminder listing documents due for removal. While off, this client is never notified — and because a
                        document is only purged after a prior month's reminder, nothing is deleted for this client either.
                    </p>
                </div>

                <Input
                    label="Audit Retention Years"
                    value={auditRetentionYears}
                    onChange={setAuditRetentionYears}
                    placeholder="e.g. 7"
                    size="sm"
                    type="number"
                />

                <div>
                    <Select
                        label="Export Mode"
                        items={EXPORT_MODE_OPTIONS}
                        selectedKey={exportMode}
                        onSelectionChange={(key) => setExportMode(String(key ?? "DirectSync"))}
                        size="sm"
                    >
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                    <p className="mt-1 text-xs text-tertiary">
                        CSV mode hides Direct Sync in the export panels and blocks bridge export jobs; the bridge (if any) keeps running vendor
                        lookups.
                    </p>
                </div>
            </div>

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}
            {success && <p className="mt-3 text-sm text-success-primary">Client updated successfully.</p>}

            <div className="mt-4">
                <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving || !hasChanges || !name.trim()}>
                    {isSaving ? "Saving..." : "Save Changes"}
                </Button>
            </div>
        </section>
    );
}

// =============================================================================
// CLIENT SETTINGS SECTION
// Bridge-style: only set keys are shown; user adds one at a time from the rest.
// BridgeConfig is excluded (managed in the Bridge tab).
// =============================================================================

const ALL_SETTINGS = [
    { key: "OCRPrompt", label: "OCR Prompt", type: "textarea", description: "System prompt used for OCR text extraction." },
    {
        key: "ClassificationPrompt",
        label: "Classification Prompt",
        type: "textarea",
        description: "Prompt for document classification (use {{DOCUMENT_TYPES}} and {{RAW_TEXT}} placeholders).",
    },
    { key: "ClassificationSchema", label: "Classification Schema", type: "json", description: "JSON schema for structured classification output." },
    {
        key: "ParsePrompt",
        label: "Parse Prompt",
        type: "textarea",
        description: "Prompt for structured field extraction (use {DocumentTypeCode} placeholder).",
    },
    {
        key: "MatchJsonPayload",
        label: "Match JSON Payload Field",
        type: "input",
        description: "Custom field code that holds the match JSON payload for receipt/PO matching.",
    },
    {
        key: "DocumentsToExport",
        label: "Documents To Export",
        type: "select",
        description: "Which related documents to include when exporting.",
        options: [
            { id: "Split", label: "Split" },
            { id: "Supporting", label: "Supporting" },
            { id: "Reference", label: "Reference" },
            { id: "Match", label: "Match" },
            { id: "Related", label: "Related" },
        ],
    },
    {
        key: "DocumentsToQueue",
        label: "Documents To Queue",
        type: "select",
        description: "Which documents to queue for workflow processing.",
        options: [
            { id: "Themselves", label: "Themselves" },
            { id: "Related", label: "Related" },
            { id: "Both", label: "Both" },
        ],
    },
] as const;

type SettingKey = (typeof ALL_SETTINGS)[number]["key"];
type ActiveSettings = Partial<Record<SettingKey, string>>;

/** Which keys have a non-empty value in the raw settings object */
function detectActiveKeys(raw: Record<string, unknown> | null | undefined): Set<SettingKey> {
    const s = raw ?? {};
    const active = new Set<SettingKey>();
    for (const { key } of ALL_SETTINGS) {
        const v = s[key];
        if (v !== undefined && v !== null && v !== "") active.add(key);
    }
    return active;
}

function readSettingValue(key: SettingKey, raw: Record<string, unknown> | null | undefined): string {
    const v = (raw ?? {})[key];
    if (key === "ClassificationSchema") {
        return v !== undefined && v !== null ? JSON.stringify(v, null, 2) : "";
    }
    return typeof v === "string" ? v : "";
}

function serializeSettingValue(key: SettingKey, value: string): unknown {
    if (key === "ClassificationSchema") {
        if (!value.trim()) return null;
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    }
    return value || null;
}

function ClientSettingsSection({ client, onClientUpdated }: GeneralTabProps) {
    const adminApi = useAdminApi();

    // Which keys are currently shown (have a value or were added)
    const [activeKeys, setActiveKeys] = useState<Set<SettingKey>>(() => detectActiveKeys(client.settings));
    // Current edited values for each active key
    const [values, setValues] = useState<ActiveSettings>(() => {
        const initial: ActiveSettings = {};
        for (const key of detectActiveKeys(client.settings)) {
            initial[key] = readSettingValue(key, client.settings);
        }
        return initial;
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Key to add next
    const [keyToAdd, setKeyToAdd] = useState<string | null>(null);

    // Sync from props when client changes
    useEffect(() => {
        const nextActive = detectActiveKeys(client.settings);
        setActiveKeys(nextActive);
        const next: ActiveSettings = {};
        for (const key of nextActive) {
            next[key] = readSettingValue(key, client.settings);
        }
        setValues(next);
    }, [client.settings]);

    // Original values for change detection
    const original = useMemo<ActiveSettings>(() => {
        const o: ActiveSettings = {};
        for (const key of detectActiveKeys(client.settings)) {
            o[key] = readSettingValue(key, client.settings);
        }
        return o;
    }, [client.settings]);

    const hasChanges = useMemo(() => {
        // A key is in original but not active → removed (changed)
        for (const key of Object.keys(original) as SettingKey[]) {
            if (!activeKeys.has(key)) return true;
        }
        // A key is active but not in original → added (changed)
        for (const key of activeKeys) {
            if (!(key in original)) return true;
            if (values[key] !== original[key]) return true;
        }
        return false;
    }, [activeKeys, values, original]);

    const handleChange = useCallback((key: SettingKey, value: string) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleRemoveKey = useCallback((key: SettingKey) => {
        setActiveKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        setValues((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const handleAddKey = useCallback(() => {
        if (!keyToAdd) return;
        const key = keyToAdd as SettingKey;
        setActiveKeys((prev) => new Set([...prev, key]));
        setValues((prev) => ({ ...prev, [key]: original[key] ?? "" }));
        setKeyToAdd(null);
    }, [keyToAdd, original]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(false);
        try {
            // Validate JSON for schema field if active
            if (activeKeys.has("ClassificationSchema")) {
                const schemaValue = (values.ClassificationSchema ?? "").trim();
                if (schemaValue) {
                    try {
                        JSON.parse(schemaValue);
                    } catch {
                        setError("Classification Schema is not valid JSON.");
                        return;
                    }
                }
            }

            const patch: Record<string, unknown> = {};
            // Keys removed: send null
            for (const key of Object.keys(original) as SettingKey[]) {
                if (!activeKeys.has(key)) patch[key] = null;
            }
            // Keys added or changed
            for (const key of activeKeys) {
                const current = values[key] ?? "";
                if (!(key in original) || current !== original[key]) {
                    patch[key] = serializeSettingValue(key, current);
                }
            }

            if (Object.keys(patch).length === 0) return;

            const dto: UpdateClientSettingsRequestDto = { settings: patch };
            await adminApi.updateClientSettings(client.id, dto);
            setSuccess(true);
            onClientUpdated();
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update settings.");
        } finally {
            setIsSaving(false);
        }
    }, [adminApi, client.id, activeKeys, values, original, onClientUpdated]);

    const handleCancel = useCallback(() => {
        const nextActive = detectActiveKeys(client.settings);
        setActiveKeys(nextActive);
        const next: ActiveSettings = {};
        for (const key of nextActive) {
            next[key] = readSettingValue(key, client.settings);
        }
        setValues(next);
        setKeyToAdd(null);
        setError(null);
    }, [client.settings]);

    // Settings not yet active (available to add)
    const availableToAdd: SelectItemType[] = ALL_SETTINGS.filter(({ key }) => !activeKeys.has(key)).map(({ key, label }) => ({ id: key, label }));

    // Ordered list of active settings (preserve ALL_SETTINGS order)
    const activeSettings = ALL_SETTINGS.filter(({ key }) => activeKeys.has(key));

    return (
        <section>
            <h3 className="text-lg font-semibold text-primary">Settings</h3>
            <p className="mt-1 text-sm text-tertiary">
                Only configured keys are shown. Add keys one at a time. <span className="font-mono text-xs">BridgeConfig</span> is managed in the Bridge tab.
            </p>

            <div className="mt-4 max-w-2xl space-y-5">
                {activeSettings.length === 0 && <p className="text-sm text-quaternary">No settings configured. Use the selector below to add one.</p>}

                {activeSettings.map((setting) => {
                    const value = values[setting.key] ?? "";
                    return (
                        <div key={setting.key} className="rounded-lg border border-secondary p-4">
                            <div className="mb-2 flex items-start justify-between gap-2">
                                <div>
                                    <span className="font-mono text-sm font-medium text-primary">{setting.key}</span>
                                    <span className="ml-2 text-sm text-secondary">{setting.label}</span>
                                    <p className="mt-0.5 text-xs text-tertiary">{setting.description}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveKey(setting.key)}
                                    className="shrink-0 text-xs text-tertiary hover:text-error-primary"
                                    title="Remove this setting"
                                >
                                    Remove
                                </button>
                            </div>
                            {setting.type === "select" && "options" in setting ? (
                                <Select
                                    items={setting.options as unknown as SelectItemType[]}
                                    selectedKey={value || null}
                                    onSelectionChange={(k) => handleChange(setting.key, String(k ?? ""))}
                                    placeholder={`Select ${setting.label}...`}
                                    size="sm"
                                >
                                    {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                                </Select>
                            ) : setting.type === "input" ? (
                                <Input value={value} onChange={(v) => handleChange(setting.key, v)} placeholder={setting.label} size="sm" />
                            ) : (
                                <TextArea
                                    value={value}
                                    onChange={(v) => handleChange(setting.key, v)}
                                    rows={setting.type === "json" ? 6 : 8}
                                    textAreaClassName="font-mono text-xs"
                                    placeholder={setting.type === "json" ? "{ ... }" : ""}
                                />
                            )}
                        </div>
                    );
                })}

                {/* Add setting row */}
                {availableToAdd.length > 0 && (
                    <div className="flex items-end gap-3 rounded-lg border border-dashed border-secondary px-4 py-3">
                        <div className="flex-1">
                            <Select
                                label="Add Setting"
                                items={availableToAdd}
                                selectedKey={keyToAdd}
                                onSelectionChange={(k) => setKeyToAdd(k ? String(k) : null)}
                                placeholder="Select a setting to add..."
                                size="sm"
                            >
                                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                            </Select>
                        </div>
                        <Button color="secondary" size="sm" onClick={handleAddKey} isDisabled={!keyToAdd}>
                            Add
                        </Button>
                    </div>
                )}
            </div>

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}
            {success && <p className="mt-3 text-sm text-success-primary">Settings updated successfully.</p>}

            <div className="mt-4 flex gap-2">
                <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving || !hasChanges}>
                    {isSaving ? "Saving..." : "Save Settings"}
                </Button>
                <Button color="secondary" size="sm" onClick={handleCancel} isDisabled={isSaving || !hasChanges}>
                    Cancel
                </Button>
            </div>
        </section>
    );
}

// =============================================================================
// ORGANIZATION SETTINGS (shared org selector for Document Types + File Types)
// =============================================================================

const UNKNOWN_CODE = "unknown";

interface OrganizationSettingsSectionProps {
    clientId: number;
}

function OrganizationSettingsSection({ clientId }: OrganizationSettingsSectionProps) {
    const adminApi = useAdminApi();

    const [organizations, setOrganizations] = useState<OrganizationAdminListItemDto[]>([]);
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    const [isOrgsLoading, setIsOrgsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load organizations on mount
    useEffect(() => {
        let cancelled = false;
        async function loadOrgs() {
            setIsOrgsLoading(true);
            try {
                const orgs = await adminApi.listClientOrganizations(clientId);
                if (cancelled) return;
                const active = orgs.filter((o) => o.isActive);
                setOrganizations(active);
                if (active.length > 0) setSelectedOrgId(active[0].id);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load organizations.");
            } finally {
                if (!cancelled) setIsOrgsLoading(false);
            }
        }
        loadOrgs();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId]);

    const orgOptions: SelectItemType[] = useMemo(
        () => organizations.map((o) => ({ id: String(o.id), label: `${o.name} (${o.code})` })),
        [organizations],
    );

    if (isOrgsLoading) return <LoadingIndicator size="sm" />;

    if (organizations.length === 0) {
        return (
            <section>
                <h3 className="text-lg font-semibold text-primary">Organization Settings</h3>
                <p className="mt-3 text-sm text-tertiary">No organizations configured. Create an organization first.</p>
            </section>
        );
    }

    return (
        <div className="space-y-10">
            <section>
                <h3 className="text-lg font-semibold text-primary">Organization Settings</h3>
                <p className="mt-1 text-sm text-tertiary">
                    Select an organization to configure. The selection applies to the Document Types and File Types sections below.
                </p>
                <div className="mt-4 max-w-xs">
                    <Select
                        label="Organization"
                        items={orgOptions}
                        selectedKey={selectedOrgId != null ? String(selectedOrgId) : null}
                        onSelectionChange={(k) => setSelectedOrgId(k ? Number(k) : null)}
                        placeholder="Select organization..."
                        size="sm"
                    >
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>
                </div>
                {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}
            </section>

            <DocumentTypesSection clientId={clientId} organizationId={selectedOrgId} />
            <FileTypesSection clientId={clientId} organizationId={selectedOrgId} />
        </div>
    );
}

// =============================================================================
// DOCUMENT TYPES SECTION
// =============================================================================

interface DocumentTypesSectionProps {
    clientId: number;
    /** Selected organization from the shared Organization Settings selector */
    organizationId: number | null;
}

function DocumentTypesSection({ clientId, organizationId }: DocumentTypesSectionProps) {
    const adminApi = useAdminApi();

    const [assignments, setAssignments] = useState<OrganizationDocumentTypeDto[]>([]);
    const [allDocumentTypes, setAllDocumentTypes] = useState<DocumentTypeAdminListItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Add row state
    const [addTypeId, setAddTypeId] = useState<number | null>(null);
    const [addIsInbound, setAddIsInbound] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);
    const [addWarnings, setAddWarnings] = useState<string[]>([]);

    // Load document type assignments when the selected org changes
    const load = useCallback(async () => {
        if (organizationId == null) return;
        setIsLoading(true);
        setError(null);
        setAddWarnings([]);
        try {
            const [assigned, all] = await Promise.all([
                adminApi.listOrganizationDocumentTypes(clientId, organizationId),
                adminApi.listDocumentTypes(),
            ]);
            setAssignments(assigned);
            setAllDocumentTypes(all);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load document types.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, organizationId]);

    useEffect(() => {
        load();
    }, [load]);

    // Document types not yet assigned (excluding unknown — always shown as locked)
    const assignedIds = new Set(assignments.map((a) => a.documentTypeId));
    const availableToAdd = allDocumentTypes.filter((dt) => !assignedIds.has(dt.id) && dt.code !== UNKNOWN_CODE);

    // Available options for the add dropdown
    const addTypeOptions: SelectItemType[] = availableToAdd.map((dt) => ({ id: String(dt.id), label: dt.name }));

    // Check if unknown is already assigned; if not, show as a locked phantom row
    const hasUnknown = assignments.some((a) => a.documentTypeCode === UNKNOWN_CODE);
    const unknownType = allDocumentTypes.find((dt) => dt.code === UNKNOWN_CODE);

    const handleToggleInbound = useCallback(
        async (assignment: OrganizationDocumentTypeDto, newValue: boolean) => {
            if (organizationId == null) return;
            // Optimistic update
            setAssignments((prev) => prev.map((a) => (a.documentTypeId === assignment.documentTypeId ? { ...a, isInbound: newValue } : a)));
            try {
                await adminApi.updateOrganizationDocumentType(clientId, organizationId, assignment.documentTypeId, { isInbound: newValue });
            } catch (err) {
                // Revert on failure
                setAssignments((prev) => prev.map((a) => (a.documentTypeId === assignment.documentTypeId ? { ...a, isInbound: assignment.isInbound } : a)));
                setError(err instanceof Error ? err.message : "Failed to update document type.");
            }
        },
        [adminApi, clientId, organizationId],
    );

    const handleRemove = useCallback(
        async (documentTypeId: number) => {
            if (organizationId == null) return;
            try {
                await adminApi.removeOrganizationDocumentType(clientId, organizationId, documentTypeId);
                setAssignments((prev) => prev.filter((a) => a.documentTypeId !== documentTypeId));
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to remove document type.");
            }
        },
        [adminApi, clientId, organizationId],
    );

    const handleAdd = useCallback(async () => {
        if (!addTypeId || organizationId == null) return;
        setIsAdding(true);
        setAddError(null);
        setAddWarnings([]);
        try {
            const dto: AddOrganizationDocumentTypeRequestDto = { documentTypeId: addTypeId, isInbound: addIsInbound };
            const created = await adminApi.addOrganizationDocumentType(clientId, organizationId, dto);
            setAssignments((prev) => [...prev, created].sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));
            setAddTypeId(null);
            setAddIsInbound(true);
            if (created.warnings && created.warnings.length > 0) {
                setAddWarnings(created.warnings);
            }
        } catch (err) {
            setAddError(err instanceof Error ? err.message : "Failed to add document type.");
        } finally {
            setIsAdding(false);
        }
    }, [adminApi, clientId, organizationId, addTypeId, addIsInbound]);

    return (
        <section>
            <h3 className="text-lg font-semibold text-primary">Document Types</h3>
            <p className="mt-1 text-sm text-tertiary">
                Configure which document types are enabled for the selected organization. <em>Unknown</em> is always present and always inbound.
            </p>

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            {isLoading ? (
                <div className="mt-4">
                    <LoadingIndicator size="sm" />
                </div>
            ) : (
                <div className="mt-4 max-w-2xl">
                    <div className="overflow-hidden rounded-lg border border-secondary">
                        {/* Table header */}
                        <div className="grid grid-cols-[1fr_auto_auto] gap-4 border-b border-secondary bg-secondary_subtle px-4 py-2 text-xs font-medium tracking-wide text-tertiary uppercase">
                            <span>Document Type</span>
                            <span className="text-center">Is Inbound</span>
                            <span />
                        </div>

                        {/* Unknown — always locked row */}
                        {!hasUnknown && (
                            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-secondary px-4 py-3 last:border-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-primary">{unknownType?.name ?? "Unknown"}</span>
                                    <span className="rounded bg-secondary_subtle px-1.5 py-0.5 font-mono text-xs text-tertiary">{UNKNOWN_CODE}</span>
                                    <span className="bg-warning-subtle rounded px-1.5 py-0.5 text-xs text-warning-primary">not assigned</span>
                                </div>
                                <div className="flex justify-center">
                                    <Toggle isSelected={true} onChange={() => {}} isDisabled size="sm" />
                                </div>
                                <div className="w-16" />
                            </div>
                        )}

                        {assignments.map((assignment) => {
                            const isUnknown = assignment.documentTypeCode === UNKNOWN_CODE;
                            return (
                                <div
                                    key={assignment.documentTypeId}
                                    className={cx(
                                        "grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-secondary px-4 py-3 last:border-0",
                                        isUnknown && "bg-secondary_subtle",
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-primary">{assignment.documentTypeName}</span>
                                        <span className="rounded bg-secondary_subtle px-1.5 py-0.5 font-mono text-xs text-tertiary">
                                            {assignment.documentTypeCode}
                                        </span>
                                        {isUnknown && (
                                            <span className="rounded bg-secondary_subtle px-1.5 py-0.5 text-xs text-quaternary italic">system</span>
                                        )}
                                    </div>
                                    <div className="flex justify-center">
                                        <Toggle
                                            isSelected={assignment.isInbound}
                                            onChange={(val) => handleToggleInbound(assignment, val)}
                                            isDisabled={isUnknown}
                                            size="sm"
                                        />
                                    </div>
                                    <div className="w-16 text-right">
                                        {!isUnknown && (
                                            <Button color="tertiary-destructive" size="sm" onClick={() => handleRemove(assignment.documentTypeId)}>
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Add row */}
                        {availableToAdd.length > 0 && (
                            <div className="border-t border-secondary bg-secondary_subtle px-4 py-3">
                                <div className="flex items-end gap-3">
                                    <div className="flex-1">
                                        <Select
                                            label="Add Document Type"
                                            items={addTypeOptions}
                                            selectedKey={addTypeId ? String(addTypeId) : null}
                                            onSelectionChange={(k) => setAddTypeId(k ? Number(k) : null)}
                                            placeholder="Select type..."
                                            size="sm"
                                        >
                                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                                        </Select>
                                    </div>
                                    <div className="pb-1">
                                        <Toggle label="Is Inbound" isSelected={addIsInbound} onChange={setAddIsInbound} size="sm" />
                                    </div>
                                    <Button color="secondary" size="sm" onClick={handleAdd} isDisabled={isAdding || !addTypeId}>
                                        {isAdding ? "Adding..." : "Add"}
                                    </Button>
                                </div>
                                {addError && <p className="mt-2 text-xs text-error-primary">{addError}</p>}
                                {addWarnings.length > 0 && (
                                    <div className="border-warning bg-warning_subtle mt-3 rounded-lg border px-4 py-3 text-sm text-warning-primary">
                                        {addWarnings.map((w, i) => (
                                            <p key={i}>{w}</p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Empty state (all types assigned) */}
                        {assignments.length === 0 && !!hasUnknown && (
                            <div className="px-4 py-6 text-center text-sm text-quaternary">No document types assigned.</div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

// =============================================================================
// FILE TYPES SECTION
// =============================================================================

interface FileTypesSectionProps {
    clientId: number;
    /** Selected organization from the shared Organization Settings selector */
    organizationId: number | null;
}

function FileTypesSection({ clientId, organizationId }: FileTypesSectionProps) {
    const adminApi = useAdminApi();

    const [rows, setRows] = useState<OrganizationFileTypeDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load file type rows when the selected org changes
    const load = useCallback(async () => {
        if (organizationId == null) return;
        setIsLoading(true);
        setError(null);
        try {
            const data = await adminApi.listOrganizationFileTypes(clientId, organizationId);
            setRows(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load file types.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, organizationId]);

    useEffect(() => {
        load();
    }, [load]);

    // Toggle state per manageable type: a configured row decides; with no row,
    // fall back to the type's default (PDF is active out of the box)
    const isActiveFor = useCallback(
        (fileTypeId: number) => {
            const row = rows.find((r) => r.fileType === fileTypeId);
            if (row) return row.isActive;
            return MANAGEABLE_FILE_TYPES.find((t) => t.id === fileTypeId)?.defaultActive ?? false;
        },
        [rows],
    );

    const handleToggle = useCallback(
        async (fileTypeId: number, newValue: boolean) => {
            if (organizationId == null) return;
            // At least one file type must stay active per organization (backend enforces the same rule)
            if (!newValue && !MANAGEABLE_FILE_TYPES.some((t) => t.id !== fileTypeId && isActiveFor(t.id))) {
                setError("At least one file type must remain active for the organization.");
                return;
            }
            setError(null);
            const previous = rows;
            // Optimistic update (upsert into local rows)
            setRows((prev) => {
                const existing = prev.find((r) => r.fileType === fileTypeId);
                if (existing) return prev.map((r) => (r.fileType === fileTypeId ? { ...r, isActive: newValue } : r));
                const catalogEntry = MANAGEABLE_FILE_TYPES.find((t) => t.id === fileTypeId);
                return [
                    ...prev,
                    {
                        fileType: fileTypeId,
                        fileTypeName: catalogEntry?.label ?? String(fileTypeId),
                        extension: catalogEntry?.extension ?? "",
                        isActive: newValue,
                        createdAt: new Date().toISOString(),
                    },
                ];
            });
            try {
                const saved = await adminApi.setOrganizationFileType(clientId, organizationId, fileTypeId, { isActive: newValue });
                setRows((prev) => prev.map((r) => (r.fileType === fileTypeId ? saved : r)));
            } catch (err) {
                // Revert on failure
                setRows(previous);
                setError(err instanceof Error ? err.message : "Failed to update file type.");
            }
        },
        [adminApi, clientId, organizationId, rows, isActiveFor],
    );

    return (
        <section>
            <h3 className="text-lg font-semibold text-primary">File Types</h3>
            <p className="mt-1 text-sm text-tertiary">
                Configure which file formats can be uploaded for the selected organization. Only active formats are accepted. <em>PDF</em> is enabled
                by default; other formats must be enabled explicitly. At least one file type must remain active.
            </p>

            {error && <p className="mt-3 text-sm text-error-primary">{error}</p>}

            {isLoading ? (
                <div className="mt-4">
                    <LoadingIndicator size="sm" />
                </div>
            ) : (
                <div className="mt-4 max-w-2xl">
                    <div className="overflow-hidden rounded-lg border border-secondary">
                        {/* Table header */}
                        <div className="grid grid-cols-[1fr_auto] gap-4 border-b border-secondary bg-secondary_subtle px-4 py-2 text-xs font-medium tracking-wide text-tertiary uppercase">
                            <span>File Type</span>
                            <span className="text-center">Is Active</span>
                        </div>

                        {/* Fixed rows — one per manageable file type */}
                        {MANAGEABLE_FILE_TYPES.map((fileType) => (
                            <div key={fileType.id} className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-secondary px-4 py-3 last:border-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-primary">{fileType.label}</span>
                                    <span className="rounded bg-secondary_subtle px-1.5 py-0.5 font-mono text-xs text-tertiary">{fileType.extension}</span>
                                </div>
                                <div className="flex justify-center">
                                    <Toggle isSelected={isActiveFor(fileType.id)} onChange={(val) => handleToggle(fileType.id, val)} size="sm" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

// =============================================================================
// USAGE SECTION
// =============================================================================

export function UsageSection({ clientId }: { clientId: number }) {
    const adminApi = useAdminApi();
    const [usage, setUsage] = useState<ClientUsageAdminDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function fetchUsage() {
            setIsLoading(true);
            try {
                const data = await adminApi.getClientUsage(clientId);
                if (!cancelled) setUsage(data);
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load usage.");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }

        fetchUsage();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId]);

    if (isLoading) return <LoadingIndicator size="sm" />;
    if (error) return <p className="text-xs text-error-primary">{error}</p>;
    if (!usage) return null;

    return (
        <div className="flex items-center gap-3">
            <StatCard label="Pages Processed" value={usage.pagesProcessed} quota={usage.pageQuota > 0 ? usage.pageQuota : undefined} />
            <StatCard label="Files Processed" value={usage.documentsProcessed} />
            <StatCard label="Billing Period" value={`${usage.billingYear}-${String(usage.billingMonth).padStart(2, "0")}`} />
        </div>
    );
}

function StatCard({ label, value, quota }: { label: string; value: string | number; quota?: number }) {
    return (
        <div className="rounded-lg border border-secondary px-3 py-2">
            <p className="text-xs font-medium text-tertiary">{label}</p>
            <p className="mt-0.5 text-sm font-semibold text-primary">
                {value}
                {quota !== undefined && <span className="text-xs font-normal text-quaternary"> / {quota}</span>}
            </p>
        </div>
    );
}

// =============================================================================
// GENERAL TAB
// =============================================================================

export function GeneralTab({ client, onClientUpdated }: GeneralTabProps) {
    return (
        <div className="space-y-10">
            <ConfigurationSection client={client} onClientUpdated={onClientUpdated} />
            <ClientSettingsSection client={client} onClientUpdated={onClientUpdated} />
            <OrganizationSettingsSection clientId={client.id} />
        </div>
    );
}
